RivalIQ.apps.Favorites.module('views.forms', function (forms, App, Backbone, Marionette, $, _) {

    'use strict';

    const DEFAULT_MAX_METRICS = 8;

    forms.Chart = Marionette.LayoutView.mixin({
        template: '#favoritesChartBuilderTemplate',

        regions: {
            metricsRegion: '.x-metrics-container'
        },

        ui: {
            types: 'select[name="focusWidgetId"]',
            primaryDimension: 'select[name="primaryDimension"]',
            dimensionValues: '.x-dimension-values-container',
            interval: '[name="groupedBy"]'
        },

        behaviors: {
            Localizable: {},
            AttributeBuilder: {}
        },

        dashboardModelEvents: {
            'changed:metrics': 'onMetricsChanged'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        onRender: function () {
            const me = this;

            me.showMetrics();

            me.intervalSelector = new App.views.selectors.Interval({
                el: me.ui.interval,
                dashboardModel: me.dashboardModel
            });
            me.intervalSelector.render();

            me.buildChartDropdown();

            me.buildDimensionDropdown();
            me.buildDimensionValuesFilter();
        },

        buildChartDropdown: function () {
            const me = this;

            me.graphSelector = new App.views.selectors.Widget({
                el: me.ui.types,
                dashboardModel: me.dashboardModel
            });

            me.graphSelector.render();
        },

        onMetricsChanged: function () {
            const me = this;
            me.graphSelector.resetItems();
        },

        showMetrics: function () {
            const me = this;

            const dashboardModel = me.getOption('dashboardModel');
            const widget = dashboardModel.getWidget();


            Marionette.bindEntityEvents(me, widget, {
                'change:showCustomSummary': 'onShowCustomSummaryChanged',
                'change:primaryDimension': 'onPrimaryDimensionChanged'
            });

            const maxMetrics = _.result(widget, ['maxMetrics']) || DEFAULT_MAX_METRICS;
            const metrics = _.take(me.getProp('metrics'), maxMetrics);
            let availableSources = widget.availableSources;
            if (_.isFunction(availableSources)) {
                availableSources = _.bind(availableSources, widget);
            }

            const widgetSupportsCustomSummary = _.result(widget, 'widgetSupportsCustomSummary');
            const customSummaryMetrics = widget.getProp('customSummaryMetrics');

            if (widgetSupportsCustomSummary && widget.getProp('showCustomSummary') && !_.isEmpty(customSummaryMetrics)) {
                _.each(metrics, (metric, idx) => {
                    metric.customSummary = _.get(customSummaryMetrics, [idx]);
                });
            }

            const metricsCollection = new App.collections.Metrics(metrics, {
                parse: true,
                baseMetric: me.dashboardModel.baseMetric,
                widget: me.dashboardModel.getWidget(),
                validChannels: availableSources
            });

            function onMetricChange() {
                me.onMetricsChange(metricsCollection);
            }

            me.listenTo(metricsCollection, 'update', onMetricChange);
            me.listenTo(metricsCollection, 'change', onMetricChange);
            me.listenTo(metricsCollection, 'sort', onMetricChange);

            me.metricsRegion.show(new App.views.MetricsView({
                collection: metricsCollection,
                dashboardModel: me.dashboardModel
            }));

            me._metricsCollection = metricsCollection;
        },

        onShowCustomSummaryChanged: function (dashboardModel) {
            const me = this;

            if (_.get(dashboardModel, ['changed', 'showCustomSummary'])) {
                // We've gone from not showing the custom summary row to showing the summary row; make sure all the metrics
                // have a sensible default.

                me._metricsCollection.each(metric => {
                    if (!metric.get('customSummary')) {

                        const validOptions = metric.validCustomSummaryOptions(me.dashboardModel.baseMetric);

                        if (validOptions.length) {
                            let customSummary;
                            const isSummable = Boolean(_.find(validOptions, {
                                id: `${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_sum`
                            }));

                            if (isSummable) {
                                customSummary = `${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_sum`;
                            } else {
                                customSummary = `${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_average`;
                            }

                            metric.set({
                                customSummary: customSummary
                            }, {
                                silent: true
                            });
                        }
                    }
                });

                me.onMetricsChange(me._metricsCollection);
            }
        },

        // This nonsense is necessary because we have the state of the metric in the builder in two places - the dashboard
        // model's "metrics" prop and me._metricsCollection. When the dimension changes, the chart's processProps makes updates
        // to it's metric, so we need to copy that update over to our version of the metric.
        onPrimaryDimensionChanged: function (dashboardModel) {
            const me = this;

            if (_.get(dashboardModel, ['changed', 'primaryDimension']) && dashboardModel.getProp('primaryDimension') === 'companySetId') {

                let dashboardModelMetrics = dashboardModel.getProp('metrics');
                dashboardModelMetrics = _.map(dashboardModelMetrics, metric => {
                    metric.dimensions = _.difference(_.without(metric.dimensions, 'companySetId'), me.dashboardModel.baseMetric.dimensions);
                    return metric;
                });

                me._metricsCollection.set(dashboardModelMetrics, {
                    parse: true
                });
            }
        },

        onAttach: function () {
            const me = this;
            me.initializePopovers();
        },

        onSummaryMetricsChanged: function (metricsCollection) {
            const me = this;

            const customSummaryMetrics = metricsCollection.map('customSummary');

            const widget = me.dashboardModel.getWidget();
            widget.setProps({
                customSummaryMetrics: customSummaryMetrics
            }, {
                navigate: false
            });
        },

        onMetricsChange: function (metricsCollection) {
            const me = this;

            me.onSummaryMetricsChanged(metricsCollection);

            const metrics = metricsCollection.toMetrics(me.dashboardModel.baseMetric);
            const props = {
                metrics: metrics
            };

            const widget = me.dashboardModel.getWidget();
            const widgetProps = _.extend({}, widget.getProps(), props);

            if (!widget.isSupported(widgetProps)) {
                return me.dashboardModel.changeWidgetId(RivalIQ.enums.widgetTypes.METRIC_TABLE, props);
            }

            if (widget.updateBaseMetric) {
                me.dashboardModel.baseMetric = widget.updateBaseMetric(me.dashboardModel.baseMetric, props);
            }

            me.setProps(props, {
                navigate: false
            });
            me.dashboardModel.trigger('changed:metrics');
        },

        serializeData: function () {
            const me = this;
            const focusWidget = me.dashboardModel.getWidget();
            const data = focusWidget.getProps();

            return _.extend({}, data, {
                showDimensionDropdown: true
            });
        },

        buildDimensionDropdown: function () {
            const me = this;

            me.primaryDimensionSelector = new App.views.selectors.Dimension({
                el: me.ui.primaryDimension,
                dashboardModel: me.dashboardModel
            });

            me.primaryDimensionSelector.render();
        },

        buildDimensionValuesFilter: function () {
            const me = this;

            me.dimensionValuesFilter = new App.views.selectors.DimensionValues({
                el: me.ui.dimensionValues,
                dashboardModel: me.dashboardModel
            });

            me.dimensionValuesFilter.render();
        },

        initializePopovers: function () {
            const me = this;
            me.$('[data-toggle="popover"]').popover({
                html: true,
                trigger: 'hover',
                placement: 'auto',
                container: 'body'
            });
        },

        updateWidget: function (props) {
            const me = this;
            me.dashboardModel.getWidget().setProps(props, {
                navigate: false
            });
        }
    });
});
